import React from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";

export default function LinkedRowSyncBanner({
  count = 0,
  onSyncAll,
  syncing = false,
  canEdit = true,
  sourceLabel = "this document",
}) {
  if (count <= 0) return null;

  const rowLabel = count === 1 ? "linked row" : "linked rows";

  return (
    <Paper
      component='section'
      aria-label='Linked schedule sync'
      aria-busy={syncing}
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        border: 1,
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.025),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <Stack
          direction='row'
          spacing={1.5}
          alignItems='flex-start'
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Avatar
            variant='rounded'
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
              color: "secondary.main",
            }}
          >
            <SyncRoundedIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box
            role='status'
            aria-atomic='true'
            sx={{ minWidth: 0, overflowWrap: "anywhere" }}
          >
            <Typography variant='body2' fontWeight={700}>
              {syncing
                ? `Syncing ${count} ${rowLabel}`
                : `${count} ${rowLabel} ${count === 1 ? "needs" : "need"} syncing`}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mt: 0.5, lineHeight: 1.6 }}
            >
              {syncing
                ? `Updating linked schedules with the latest names and amounts from ${sourceLabel}.`
                : canEdit
                  ? `Update linked schedules with the latest names and amounts from ${sourceLabel}.`
                  : `Names or amounts in ${sourceLabel} have changed. An editor can update the linked schedules.`}
            </Typography>
          </Box>
        </Stack>
        {canEdit && (
          <Button
            size='small'
            variant='contained'
            startIcon={
              syncing ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <SyncRoundedIcon fontSize='small' />
              )
            }
            disabled={syncing || typeof onSyncAll !== "function"}
            onClick={() => {
              void Promise.resolve(onSyncAll?.()).catch(() => {});
            }}
            sx={{
              minHeight: 38,
              px: 2,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              bgcolor: (theme) =>
                syncing
                  ? alpha(theme.palette.secondary.main, 0.12)
                  : theme.palette.secondary.main,
              color: (theme) =>
                syncing
                  ? alpha(theme.palette.secondary.main, 0.8)
                  : theme.palette.secondary.contrastText,
              "&:hover": {
                bgcolor: (theme) =>
                  syncing
                    ? alpha(theme.palette.secondary.main, 0.12)
                    : theme.palette.secondary.dark,
              },
            }}
          >
            {syncing ? "Syncing..." : "Sync changes"}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
